import React from 'react';

function formatYearMonthShort(yearMonth) {
  if (!yearMonth) return '';
  const [y, m] = yearMonth.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-US', { month: 'short' });
}

function prevYearMonth(yearMonth) {
  const [y, m] = yearMonth.split('-').map(Number);
  if (m === 1) return `${y - 1}-12`;
  return `${y}-${String(m - 1).padStart(2, '0')}`;
}

const Sep = () => <span className="text-blue-400/80 select-none">·</span>;

/**
 * Monthly ledger leave balance — compact one-line summary.
 * @param {{ balance: object|null, compact?: boolean, availableOnly?: boolean, className?: string }} props
 */
const LeaveBalanceSummary = ({ balance, compact = false, availableOnly = false, className = '' }) => {
  if (!balance) return null;

  const available = parseFloat(balance.available_to_use_now ?? balance.remaining ?? 0);
  const exitAccrual = parseFloat(balance.exit_leave_accrual ?? 0);
  const used = parseFloat(balance.used_days ?? 0);
  const excess = parseFloat(balance.excess_days ?? 0);
  const frozenCarry = parseFloat(balance.frozen_carry_in ?? 0);
  const inProbation = Boolean(balance.in_probation);
  const cutoverMonth = balance.ledger_cutover_month || '2026-06';
  const openingStored = parseFloat(balance.opening_balance_stored ?? 0);
  const carryIn = parseFloat(balance.carry_in ?? 0);
  const monthlyCredit = parseFloat(balance.monthly_credit ?? 0);
  const isCutoverMonth = balance.year_month === cutoverMonth;
  const showLedgerBreakdown = !inProbation && (balance.year_month >= cutoverMonth || openingStored > 0);
  const openingShort = formatYearMonthShort(prevYearMonth(cutoverMonth));
  const monthShort = formatYearMonthShort(balance.year_month);
  const poolInValue = isCutoverMonth ? openingStored : carryIn;

  if (availableOnly) {
    return (
      <div
        className={`flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 ${className}`}
      >
        <span className="text-sm font-medium text-blue-800">Available this month</span>
        <span className="text-lg font-bold tabular-nums text-primary-700">
          {available.toFixed(1)} <span className="text-sm font-semibold">days</span>
        </span>
      </div>
    );
  }

  if (compact) {
    return (
      <span className={`text-xs tabular-nums ${className}`}>
        {available.toFixed(1)} avail
        {exitAccrual > 0 ? ` · ${exitAccrual.toFixed(1)} exit` : ''}
      </span>
    );
  }

  return (
    <div className={`bg-blue-50 border border-blue-200 rounded px-2 py-1 text-xs text-blue-900 ${className}`}>
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 leading-snug">
        <span className="font-medium whitespace-nowrap">{balance.leave_type_name}</span>

        {inProbation && (
          <>
            <Sep />
            <span className="text-amber-800 whitespace-nowrap">probation</span>
          </>
        )}

        {showLedgerBreakdown && (
          <>
            <Sep />
            <span className="tabular-nums whitespace-nowrap">
              {isCutoverMonth ? `Open ${openingShort}` : 'Fwd'} {poolInValue.toFixed(1)}
            </span>
            <Sep />
            <span className="tabular-nums text-green-700 whitespace-nowrap">
              +{monthlyCredit.toFixed(1)} {monthShort}
            </span>
            <Sep />
            <span className="tabular-nums whitespace-nowrap">Used {used.toFixed(1)}</span>
          </>
        )}

        {!showLedgerBreakdown && !inProbation && (
          <>
            {balance.carry_in != null && (
              <>
                <Sep />
                <span className="tabular-nums whitespace-nowrap">
                  Carry {parseFloat(balance.carry_in || 0).toFixed(1)}
                </span>
              </>
            )}
            {balance.monthly_credit != null && (
              <>
                <Sep />
                <span className="tabular-nums text-green-700 whitespace-nowrap">
                  +{parseFloat(balance.monthly_credit || 0).toFixed(1)}
                </span>
              </>
            )}
            <Sep />
            <span className="tabular-nums whitespace-nowrap">Used {used.toFixed(1)}</span>
          </>
        )}

        {inProbation && frozenCarry > 0 && (
          <>
            <Sep />
            <span className="tabular-nums whitespace-nowrap">Frozen {frozenCarry.toFixed(1)}</span>
          </>
        )}

        <Sep />
        <span className="font-semibold tabular-nums text-primary-700 whitespace-nowrap">
          {available.toFixed(1)} avail
        </span>

        {exitAccrual > 0 && (
          <>
            <Sep />
            <span className="tabular-nums whitespace-nowrap">{exitAccrual.toFixed(1)} exit</span>
          </>
        )}

        {excess > 0 && (
          <>
            <Sep />
            <span className="tabular-nums text-red-700 whitespace-nowrap">
              {inProbation ? 'Deduct' : 'Excess'} {excess.toFixed(1)}
            </span>
          </>
        )}
      </div>
    </div>
  );
};

export default LeaveBalanceSummary;
